import React, { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';

import { useService } from 'hooks';
import useLmsService from 'services/lmsService/useLmsService';
import { IGetAssessmentDataRequest, IGetAssessmentDataResponse, IAssessment, IAssessmentQuestion } from 'services/lmsService/lmsServiceTypes';
import { getPortalUrl } from 'components/Portal/Utilities';
import { useSelector } from 'react-redux';
import { urlSelector } from 'PortalStore/Selectors/EnvDataSelectors';

interface ILmsContextProviderProps {
  assessmentType: string;
}

interface ILmsContextType {
  getAssessmentDataRequest: IGetAssessmentDataRequest;
  assessment: IAssessment;
  questions: Array<IAssessmentQuestion>;
  dashboardUrl: string;
  percentCorrect: number;
  passed: boolean;
  submissionInProgress: boolean;

  checkAnswers: (question: IAssessmentQuestion, selectedAnswerIds: Array<string>) => boolean;
  setPercentCorrect: (percentCorrect: number) => void;
  setPassed: (passed: boolean) => void;
  setSubmissionInProgress: (submissionInProgress: boolean) => void;
  setAssessment: (assessment: IAssessment) => void;
}

export const AccreditationConstants = {
  COMMERCIAL_ASSESSMENT_TYPE: 'RPA Excluded Assessment',
  RECREATIONAL_ASSESSMENT_TYPE: 'RPA Recreational Assessment',
  SINGLE_ANSWER_TYPE_CRM: 852460000
};

export const LmsContext = React.createContext<ILmsContextType>({} as ILmsContextType);

export const LmsContextProvider: React.FC<ILmsContextProviderProps> = ({ children, assessmentType }) => {
  const lmsService = useLmsService();
  const [assessment, setAssessment] = useState<IAssessment>(null);
  const [questions, setQuestions] = useState<Array<IAssessmentQuestion>>([]);
  const [percentCorrect, setPercentCorrect] = useState<number>(0);
  const [passed, setPassed] = useState<boolean>(false);
  const [submissionInProgress, setSubmissionInProgress] = useState<boolean>(false);

  const request: IGetAssessmentDataRequest = {
    assessmentType: assessmentType,
    limitQuestionsPerCategory: true
  };

  const assessmentDataResponse = useService<IGetAssessmentDataResponse>(() => lmsService.getAssessmentData(request));

  const B2CSettings = useSelector((state: any) => state.B2CSettings);
  const urlHome = useSelector((state: any) => urlSelector(state, 'Portal - Home URL'));
  const getDashboardUrl = () => {
    return getPortalUrl('/dashind', B2CSettings.items.standalone, urlHome);
  };
  const dashboardUrl = getDashboardUrl();

  useEffect(() => {
    if (assessmentDataResponse && assessmentDataResponse.assessment) {
      setAssessment(assessmentDataResponse.assessment);
    }
  }, [assessmentDataResponse.assessment]);

  useEffect(() => {
    if (assessment) {
      processQuestions(assessment);
    }
  }, [assessment]);

  const processQuestions = (assessment: IAssessment) => {
    let questions: Array<IAssessmentQuestion> = [];
    assessment?.categories.forEach(category => questions = category.questions.concat(questions));

    // Shuffle questions
    setQuestions(shuffle(questions));
  };

  // TODO: ideally this would be a common utility but there isn't currently a pattern for this
  const shuffle = <T extends unknown>(array: Array<T>) => {
    let currentIndex = array.length, randomIndex: number;

    while (currentIndex != 0) {
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;

      [array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]];
    }

    return array;
  };

  /**
   * Checks if the selected answers are correct.
   *
   * @param {IAssessmentQuestion} question Question to check answer for.
   * @param {Array<string>} selectedAnswerIds - The selected answers to check.
   * @return {boolean} Returns true when only all correct answers were selected.
   */
  const checkAnswers = (question: IAssessmentQuestion, selectedAnswerIds: Array<string>): boolean => question.answers.every(answer =>
    (!answer.isCorrect && !selectedAnswerIds?.includes(answer.id)) || (answer.isCorrect && selectedAnswerIds?.includes(answer.id)));

  // Redirect back to selector if there were issues loading questions
  // TODO: this should display an error message on the form
  if (!assessmentDataResponse.loading && assessmentDataResponse.errors) {
    return (
      <Redirect to='/drone-accreditation?error=assessment_data' />
    );
  }

  return (
    <LmsContext.Provider value={{
      getAssessmentDataRequest: request,
      assessment: assessment,
      questions: questions,
      dashboardUrl: dashboardUrl,
      percentCorrect: percentCorrect,
      passed: passed,
      submissionInProgress: submissionInProgress,

      checkAnswers,
      setPercentCorrect,
      setPassed,
      setSubmissionInProgress,
      setAssessment
    }}>
      {children}
    </LmsContext.Provider>
  );
};